import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider, appleProvider, getFirestoreDb } from '../services/firebase';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  updateProfile as firebaseUpdateProfile,
  User as FirebaseUser,
  signInWithRedirect,
  verifyBeforeUpdateEmail,
  deleteUser,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { doc, deleteDoc } from 'firebase/firestore';
import { resolveType } from '@/services/ServiceContainer';
import { UserService } from '@/services/UserService';

// Type for redirect state
interface RedirectState {
  wasLoggedIn: boolean;
  redirectPath: string;
  timestamp: number;
}

export interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    displayName: string,
    nickName?: string,
    suffix?: string
  ) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateCustomEmail: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  updateProfile: (data: {
    displayName?: string;
    photoURL?: string;
  }) => Promise<void>;
  signInWithGoogle: (redirectPath?: string) => Promise<void>;
  signInWithApple: (redirectPath?: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Reads the redirect state from localStorage
  const getRedirectState = (): RedirectState | null => {
    const stateJson = localStorage.getItem('authRedirectState');
    if (!stateJson) return null;

    try {
      return JSON.parse(stateJson) as RedirectState;
    } catch (e) {
      console.error('Error parsing redirect state:', e);
      return null;
    }
  };

  // Clears the redirect state
  const clearRedirectState = () => {
    localStorage.removeItem('authRedirectState');
  };

  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    displayName: string,
    nickName?: string,
    suffix?: string
  ) => {
    try {
      console.log('Starting sign up process...');

      // Create the Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      console.log('Created Firebase Auth user:', user.uid);

      // Update the user's display name in Auth
      await firebaseUpdateProfile(user, { displayName });
      console.log('Updated user profile with display name');

      // Store additional user data in localStorage to be used by onAuthStateChanged
      const pendingUserData = {
        firstName,
        lastName,
        nickName,
        suffix,
        displayName
      };
      console.log('Storing pending user data:', pendingUserData);
      localStorage.setItem('pendingUserData', JSON.stringify(pendingUserData));

      // Force a refresh of the auth state
      await auth.currentUser?.reload();
      console.log('Reloaded auth state');
    } catch (error) {
      console.error('Error during sign up:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to create account: ${error.message}`);
      }
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setUser(user);
      setLoading(false);

      // Check if we have redirect state stored
      const redirectState = getRedirectState();

      // If we have redirect state and the auth state changed (was not logged in, now is)
      if (redirectState && !redirectState.wasLoggedIn && user) {
        console.log('Detected successful login with redirect state');
        clearRedirectState();
        navigate(redirectState.redirectPath);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const signInWithEmail = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in with email:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to sign in: ${error.message}`);
      }
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      const userService = resolveType(UserService);
      await userService.signOut();

    } catch (error) {
      console.error('Error signing out:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to sign out: ${error.message}`);
      }
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `https://${import.meta.env.VITE_FIREBASE_AUTH_DOMAIN}/login?email=${encodeURIComponent(email)}`,
      });
    } catch (error) {
      console.error('Error sending password reset email:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to send password reset email: ${error.message}`);
      }
      throw error;
    }
  };

  const updateCustomEmail = async (email: string) => {
    if (!user) throw new Error('No user is signed in');

    try {
      await verifyBeforeUpdateEmail(user, email);
    } catch (error) {
      console.error('Error updating email:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to update email: ${error.message}`);
      }
      throw error;
    }
  };

  const updatePassword = async (password: string) => {
    if (!user) throw new Error('No user is signed in');

    try {
      await firebaseUpdatePassword(user, password);
    } catch (error) {
      console.error('Error updating password:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to update password: ${error.message}`);
      }
      throw error;
    }
  };

  const updateProfile = async (data: {
    displayName?: string;
    photoURL?: string;
  }) => {
    if (!user) throw new Error('No user is signed in');

    try {
      await firebaseUpdateProfile(user, data);
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to update profile: ${error.message}`);
      }
      throw error;
    }
  };

  const signInWithGoogle = async (redirectPath?: string) => {
    try {
      // Store the current auth state and redirect path
      const redirectState: RedirectState = {
        wasLoggedIn: !!auth.currentUser,
        redirectPath: redirectPath || '/dashboard',
        timestamp: Date.now()
      };
      localStorage.setItem('authRedirectState', JSON.stringify(redirectState));

      // Set persistence to local
      await setPersistence(auth, browserLocalPersistence);

      // Start the Google sign-in process
      await signInWithRedirect(auth, googleProvider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to sign in with Google: ${error.message}`);
      }
      throw error;
    }
  };

  const signInWithApple = async (redirectPath?: string) => {
    try {
      // Store the current auth state and redirect path
      const redirectState: RedirectState = {
        wasLoggedIn: !!auth.currentUser,
        redirectPath: redirectPath || '/dashboard',
        timestamp: Date.now()
      };
      localStorage.setItem('authRedirectState', JSON.stringify(redirectState));

      // Set persistence to local
      await setPersistence(auth, browserLocalPersistence);

      // Start the Apple sign-in process
      await signInWithRedirect(auth, appleProvider);
    } catch (error) {
      console.error('Error signing in with Apple:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to sign in with Apple: ${error.message}`);
      }
      throw error;
    }
  };

  const deleteAccount = async () => {
    if (!user) throw new Error('No user is signed in');

    try {
      // Delete the user document from Firestore
      const userDocRef = doc(getFirestoreDb(), 'users', user.uid);
      await deleteDoc(userDocRef);

      // Delete the user from Firebase Auth
      await deleteUser(user);
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error instanceof Error) {
        throw new Error(`Failed to delete account: ${error.message}`);
      }
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithEmail,
        signOut,
        signUp,
        resetPassword,
        updateCustomEmail,
        updatePassword,
        updateProfile,
        signInWithGoogle,
        signInWithApple,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
